import { ISupplierExposure } from '@sp-types/FutureOrderType';
import { MonthYear } from '@utils/MonthYear/monthYear';
import { MonthYearValue } from '@utils/MonthYear/MonthYearValue';

export const accumulateMaterialExposure = (
  productMaterialExposure: ISupplierExposure[],
  startingDate?: MonthYear,
): { materialId: string; exposureCalendar: MonthYearValue[] }[] => {
  return productMaterialExposure.map((exposure) => {
    const filledExposureCalendar = fillMissingMonths(exposure.exposureCalendar, startingDate);

    const { totalValue, monthYearMap } = filledExposureCalendar.reduce(
      (acc, monthYearValue) => {
        const key = `${monthYearValue.date.year}-${monthYearValue.date.month}`;
        acc.totalValue += monthYearValue.value;

        if (!acc.monthYearMap.has(key)) {
          acc.monthYearMap.set(key, 0);
        }
        acc.monthYearMap.set(key, acc.monthYearMap.get(key)! + monthYearValue.value);
        return acc;
      },
      { totalValue: 0, monthYearMap: new Map<string, number>() },
    );

    const initialState = {
      cumulativeValue: totalValue,
      exposureCalendar: [] as { value: number; date: MonthYear }[],
    };

    const { exposureCalendar } = filledExposureCalendar.reduce((acc, monthYearValue) => {
      const key = `${monthYearValue.date.year}-${monthYearValue.date.month}`;
      const value = monthYearMap.get(key)!;

      const newValue = {
        value: acc.cumulativeValue,
        date: monthYearValue.date,
      };

      acc.cumulativeValue -= value;
      acc.exposureCalendar.push(newValue);

      return acc;
    }, initialState);

    return {
      materialId: exposure.materialId,
      exposureCalendar,
    };
  });
};

export const fillMissingMonths = (data: MonthYearValue[], startingDate?: MonthYear): MonthYearValue[] => {
  if (data.length === 0) return [];

  const sortedData = data.sort(
    (a, b) => new Date(a.date.year, a.date.month - 1).getTime() - new Date(b.date.year, b.date.month - 1).getTime(),
  );

  const firstDataDate = new Date(sortedData[0].date.year, sortedData[0].date.month - 1);
  const startDate = startingDate ? new Date(startingDate.year, startingDate.month) : firstDataDate;

  const adjustedStartDate = startDate > firstDataDate ? firstDataDate : startDate;

  const endDate = new Date(
    sortedData[sortedData.length - 1].date.year,
    sortedData[sortedData.length - 1].date.month - 1,
  );

  const result: MonthYearValue[] = [];
  let currentIndex = 0;

  for (let date = adjustedStartDate; date <= endDate; date.setMonth(date.getMonth() + 1)) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    if (
      currentIndex < sortedData.length &&
      sortedData[currentIndex].date.year === year &&
      sortedData[currentIndex].date.month === month
    ) {
      result.push(sortedData[currentIndex++]);
    } else {
      result.push({ value: 0, date: { year, month } });
    }
  }

  return result;
};
